/* ============================================================================
   MOBILE STICKY ADD TO CART — GW CART v6
   ----------------------------------------------------------------------------
   This script never talks to the Cart API and never opens the cart drawer
   itself. It only:
     1. Mirrors the real PDP quantity input <-> the sticky quantity input.
     2. Proxy-clicks the real, existing [name="add"] submit button so that
        product-form.js (theme-native) runs exactly as it always has:
        same AJAX add, same cart-drawer.renderContents(), same cart icon
        bubble update, same upsells/notifications. Nothing is duplicated.
     3. Mirrors the real button's disabled / sold-out state onto the sticky
        button via a MutationObserver, so variant changes and stock limits
        are always correct without re-implementing that logic.
     4. Uses one IntersectionObserver per product section to show/hide the
        bar based on the real Add to Cart block's visibility.

   Deliberately does NOT show any loading/click animation on the sticky
   button itself (no spinner, no label swap, no dimming) — tapping it looks
   exactly the same before, during and after the add-to-cart request.

   Performance: no scroll listeners, no polling, no external libraries.
   Runs only on mobile (max-width: 749px) and only when the merchant has the
   "Enable Mobile Sticky Add to Cart" theme setting ON (in which case this
   file isn't even requested).
   ============================================================================ */
(function () {
  'use strict';

  if (window.MobileStickyAtcLoaded) return;
  window.MobileStickyAtcLoaded = true;

  var MOBILE_QUERY = '(max-width: 749px)';

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function initRoot(root) {
    if (!root || root.dataset.stickyAtcBound === 'true') return;

    var sectionId = root.dataset.section;
    var formId = root.dataset.productFormId;
    var form = formId ? document.getElementById(formId) : null;
    if (!form) return; // no product-form on this page (e.g. no buy_buttons block)

    var realSubmit = form.querySelector('[name="add"]');
    var realQuantityInput = document.getElementById('Quantity-' + sectionId);
    var anchor = document.getElementById('ProductSubmit-' + sectionId);

    // Without the real button + the scroll anchor there is nothing safe to
    // proxy into — leave the bar hidden and do nothing else.
    if (!realSubmit || !anchor) return;

    root.dataset.stickyAtcBound = 'true';

    var stickyQuantityInput = root.querySelector('[data-sticky-quantity] input');
    var stickySubmit = root.querySelector('[data-sticky-add-to-cart]');
    var stickyLabel = root.querySelector('[data-sticky-label]');

    var isVisible = false;
    var syncingFromReal = false;
    var syncingFromSticky = false;

    /* ---------------------------------------------------------------------
       Quantity: two-way sync with the real PDP quantity input.
       quantity-input (global.js) already dispatches a bubbling 'change' on
       its own <input> whenever +/- is pressed, and the real quantity input
       also gets a native 'change' on manual typing — both are covered here.
       ------------------------------------------------------------------- */
    if (stickyQuantityInput && realQuantityInput) {
      stickyQuantityInput.value = realQuantityInput.value || '1';

      stickyQuantityInput.addEventListener('change', function () {
        if (syncingFromReal) return;
        var value = parseInt(stickyQuantityInput.value, 10);
        if (!value || value < 1) value = 1;
        stickyQuantityInput.value = value;

        if (String(realQuantityInput.value) !== String(value)) {
          syncingFromSticky = true;
          realQuantityInput.value = value;
          realQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          syncingFromSticky = false;
        }
      });

      realQuantityInput.addEventListener('change', function () {
        if (syncingFromSticky) return;
        if (String(stickyQuantityInput.value) !== String(realQuantityInput.value)) {
          syncingFromReal = true;
          stickyQuantityInput.value = realQuantityInput.value;
          syncingFromReal = false;
        }
      });
    }

    /* ---------------------------------------------------------------------
       Add to cart: proxy a real click on the theme's own submit button.
       The real product-form custom element (product-form.js) owns the
       fetch to routes.cart_add_url, the loading state, the error message,
       and calling cart-drawer.renderContents() — none of that is touched
       or duplicated here.
       ------------------------------------------------------------------- */
    if (stickySubmit) {
      stickySubmit.addEventListener('click', function () {
        if (stickySubmit.disabled) return;
        if (realSubmit.getAttribute('aria-disabled') === 'true') return; // already mid-request

        // Deliberately no visual change here: no spinner, no label swap,
        // no dimming. The button proxies the click and looks exactly the
        // same before, during, and after the add-to-cart request — the
        // real button's own aria-disabled guard (checked above) is what
        // stops a rapid second tap from firing a duplicate request.
        realSubmit.click();
      });
    }

    /* ---------------------------------------------------------------------
       Mirror state from the real button: sold-out/unavailable and label
       text. This is what keeps the sticky bar correct across variant
       changes without re-implementing variant-selects logic — global.js's
       VariantSelects/VariantRadios already flips `disabled` and the
       button's label text on the real button; we just watch it.

       Deliberately NOT mirrored: the real button's transient "loading"
       state. The sticky button's label and look must stay exactly the
       same when tapped — only genuine sold-out/unavailable changes the
       sticky button.
       ------------------------------------------------------------------- */
    function syncFromRealButton() {
      var soldOut = realSubmit.disabled || realSubmit.hasAttribute('disabled');
      var loading =
        realSubmit.classList.contains('loading') ||
        realSubmit.getAttribute('aria-disabled') === 'true';

      if (stickySubmit) {
        stickySubmit.disabled = soldOut;
      }

      // Skip copying the label while a request is in flight, in case the
      // theme briefly swaps the real button's text (e.g. "Adding...") —
      // the sticky label should never flicker to that and back.
      var realLabelSpan = realSubmit.querySelector('span');
      var realText = realLabelSpan ? realLabelSpan.textContent.trim() : '';
      if (stickyLabel && realText && !loading) {
        stickyLabel.textContent = realText;
      }
    }

    var buttonObserver = new MutationObserver(debounce(syncFromRealButton, 30));
    buttonObserver.observe(realSubmit, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true
    });

    syncFromRealButton();

    /* ---------------------------------------------------------------------
       Show / hide: IntersectionObserver on the real Add to Cart block.
       Show only once the user has scrolled *past* it (its top edge is
       above the viewport), hide as soon as it's back in view. This avoids
       showing the sticky bar while the section simply hasn't been reached
       yet (e.g. a tall gallery above the fold on first load).
       ------------------------------------------------------------------- */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          var entry = entries[entries.length - 1];
          var shouldShow = entry.isIntersecting ? false : entry.boundingClientRect.top < 0;
          if (shouldShow !== isVisible) {
            isVisible = shouldShow;
            root.classList.toggle('is-visible', isVisible);
          }
        },
        { root: null, threshold: 0, rootMargin: '0px' }
      );
      io.observe(anchor);
    }

    root.removeAttribute('hidden');
  }

  function boot() {
    if (!window.matchMedia(MOBILE_QUERY).matches) return;
    var roots = document.querySelectorAll('[data-mobile-sticky-atc]');
    for (var i = 0; i < roots.length; i++) {
      initRoot(roots[i]);
    }
  }

  var debouncedBoot = debounce(boot, 150);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-check on viewport changes (e.g. orientation change crossing the
  // mobile breakpoint) — initRoot() is idempotent per section via the
  // data-sticky-atc-bound guard, so this never double-binds.
  window.addEventListener('resize', debouncedBoot);

  // Theme editor / dynamic section reloads.
  document.addEventListener('shopify:section:load', boot);
})();
